/* Monetisation — compact layout (option 1b). One page per strategy: the
   essentials, then an "Advanced" disclosure that stacks the rest as titled
   sections. Every field saves on change (debounced); the rail toggle is the
   strategy's Enabled switch and saves immediately. */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type {
  Account,
  CallPolicy,
  CallPolicyBody,
  CustomPolicy,
  CustomPolicyBody,
  HotTipPolicyBody,
  NormalTipPolicyBody,
  SubOfferPolicyBody,
  VipOfferPolicyBody,
} from '../../../../api/client';
import { usePolicy, patchNow, type Policy } from '../usePolicy';
import { defaultCustomBody } from '../../CustomRequests/defaults';
import { defaultCallBody } from '../../VoiceCalls/defaults';
import { ErrorLine, Loading, PageFrame, SaveStatus, SectionRail, type RailRow } from '../kit';
import { HOT_DEFAULTS, NORMAL_DEFAULTS, SUB_DEFAULTS, VIP_DEFAULTS, hotBodyOf, normalBodyOf, subBodyOf, vipBodyOf } from './model';
import { HotPage, NormalPage, VipPage, SubPage, type Patch } from './TipStrategies';
import { CustomPage, CallsPage } from './OrderStrategies';
import { callRowsFrom, customRowsFrom, callDict, customDict, type CallRow, type CustomRow } from './orderRows';

type Id = 'hot' | 'normal' | 'vip' | 'sub' | 'custom' | 'calls';
const IDS: Id[] = ['hot', 'normal', 'vip', 'sub', 'custom', 'calls'];

function customBodyOf(p: CustomPolicy): CustomPolicyBody {
  return {
    is_active: p.is_active, custom_request_mode: p.custom_request_mode, custom_pricings: p.custom_pricings || {},
    photo_delivery_eta: p.photo_delivery_eta, video_delivery_eta: p.video_delivery_eta,
    tip_tolerance: typeof p.tip_tolerance === 'number' ? p.tip_tolerance : 2, max_negotiation_attempts: p.max_negotiation_attempts,
    strategy_prompt: p.strategy_prompt, pricing_guidance: p.pricing_guidance, decline_note: p.decline_note ?? null,
  };
}
function callBodyOf(p: CallPolicy): CallPolicyBody {
  return {
    is_active: p.is_active, call_request_mode: p.call_request_mode, call_pricings: p.call_pricings || {},
    call_availability: p.call_availability, pricing_guidance: p.pricing_guidance,
    tip_tolerance: typeof p.tip_tolerance === 'number' ? p.tip_tolerance : 2, max_negotiation_attempts: p.max_negotiation_attempts,
    strategy_prompt: p.strategy_prompt, decline_note: p.decline_note ?? null,
  };
}

export function MonetisationPage({ account }: { account: Account }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tr } = useTranslation('customRequests');
  const { t: tc } = useTranslation('voiceCalls');
  const id = account.account_id;
  const [params, setParams] = useSearchParams();
  const raw = params.get('s') as Id | null;
  const sel: Id = raw && IDS.includes(raw) ? raw : 'hot';
  const select = (s: string) => {
    const next = new URLSearchParams(params);
    next.set('s', s);
    setParams(next, { replace: true });
  };

  const hot = usePolicy<HotTipPolicyBody>(() => api.getHotTipPolicy(id).then(hotBodyOf), (b) => api.putHotTipPolicy(id, b).then(hotBodyOf), HOT_DEFAULTS, id);
  const normal = usePolicy<NormalTipPolicyBody>(() => api.getNormalTipPolicy(id).then(normalBodyOf), (b) => api.putNormalTipPolicy(id, b).then(normalBodyOf), NORMAL_DEFAULTS, id);
  const vip = usePolicy<VipOfferPolicyBody>(() => api.getVipOfferPolicy(id).then(vipBodyOf), (b) => api.putVipOfferPolicy(id, b).then(vipBodyOf), VIP_DEFAULTS, id);
  const sub = usePolicy<SubOfferPolicyBody>(() => api.getSubOfferPolicy(id).then(subBodyOf), (b) => api.putSubOfferPolicy(id, b).then(subBodyOf), SUB_DEFAULTS, id);

  // Customs & calls keep their table rows as an ordered list while editing (a dict
  // can't hold a half-typed or duplicate key) and serialise on every change. A
  // brand-new policy starts from the template, which isn't saved until edited.
  const [customRows, setCustomRows] = useState<CustomRow[]>([]);
  const custom = usePolicy<CustomPolicyBody>(
    () => api.getCustomPolicy(id).then((p) => {
      const b = p.exists ? customBodyOf(p) : defaultCustomBody(tr);
      setCustomRows(customRowsFrom(b.custom_pricings));
      return b;
    }),
    (b) => api.putCustomPolicy(id, b).then(customBodyOf),
    defaultCustomBody(tr),
    id,
  );
  const [callRows, setCallRows] = useState<CallRow[]>([]);
  const calls = usePolicy<CallPolicyBody>(
    () => api.getCallPolicy(id).then((p) => {
      const b = p.exists ? callBodyOf(p) : defaultCallBody(tc);
      setCallRows(callRowsFrom(b.call_pricings));
      return b;
    }),
    (b) => api.putCallPolicy(id, b).then(callBodyOf),
    defaultCallBody(tc),
    id,
  );
  // Rows → dict on every edit.
  const setCustomRowsSync = (rows: CustomRow[]) => { setCustomRows(rows); custom.patch({ custom_pricings: customDict(rows) }); };
  const setCallRowsSync = (rows: CallRow[]) => { setCallRows(rows); calls.patch({ call_pricings: callDict(rows) }); };

  const [adv, setAdv] = useState<Record<Id, boolean>>({ hot: false, normal: false, vip: false, sub: false, custom: false, calls: false });
  const togAdv = () => setAdv((a) => ({ ...a, [sel]: !a[sel] }));

  const toggleRow = <T extends { is_active: boolean }>(p: Policy<T>): Pick<RailRow, 'on' | 'onToggle'> => ({
    on: p.state === 'ready' ? p.body.is_active : null,
    onToggle: (v) => patchNow(p, { is_active: v } as Partial<T>),
  });

  const rail: RailRow[] = [
    { id: 'hot', label: t('mon.s.hot'), ...toggleRow(hot) },
    { id: 'normal', label: t('mon.s.normal'), ...toggleRow(normal) },
    { id: 'vip', label: t('mon.s.vip'), ...toggleRow(vip) },
    { id: 'sub', label: t('mon.s.sub'), ...toggleRow(sub) },
    { id: 'custom', label: t('mon.s.custom') },
    { id: 'calls', label: t('mon.s.calls') },
  ];

  const cur = { hot, normal, vip, sub, custom, calls }[sel];
  const open = adv[sel];
  const body = (() => {
    switch (sel) {
      case 'hot': return <HotPage d={hot.body} set={hot.patch as Patch<HotTipPolicyBody>} adv={open} togAdv={togAdv} />;
      case 'normal': return <NormalPage d={normal.body} set={normal.patch as Patch<NormalTipPolicyBody>} adv={open} togAdv={togAdv} />;
      case 'vip': return <VipPage d={vip.body} set={vip.patch as Patch<VipOfferPolicyBody>} adv={open} togAdv={togAdv} />;
      case 'sub': return <SubPage accountId={id} d={sub.body} set={sub.patch as Patch<SubOfferPolicyBody>} adv={open} togAdv={togAdv} />;
      case 'custom': return <CustomPage d={custom.body} set={custom.patch as Patch<CustomPolicyBody>} rows={customRows} setRows={setCustomRowsSync} adv={open} togAdv={togAdv} />;
      case 'calls': return <CallsPage d={calls.body} set={calls.patch as Patch<CallPolicyBody>} rows={callRows} setRows={setCallRowsSync} adv={open} togAdv={togAdv} />;
    }
  })();

  return (
    <PageFrame
      rail={<SectionRail heading={t('mon.rail')} items={rail} active={sel} onSelect={select} />}
      headline={t(`mon.s.${sel}`)}
      desc={t(`mon.d.${sel}`)}
      right={<SaveStatus status={cur.auto.status} error={cur.auto.error} onRetry={cur.auto.retry} idleLabel={cur.state === 'ready'} />}
    >
      {cur.state === 'loading' && <Loading />}
      {cur.state === 'failed' && <ErrorLine>{t('loadFailed')}</ErrorLine>}
      {cur.state === 'ready' && body}
    </PageFrame>
  );
}
